import {
  formatOperatingCostAmount,
  formatOperatingCostDateTime,
  formatOperatingCostPaymentFeeOrigin,
  formatOperatingCostPaymentFeeScope,
  formatOperatingCostRate,
} from '../lib/operating-costs'
import styles from '../CustosOperacionaisPage.module.css'
import { usePaymentFeePostings } from '../hooks/usePaymentFeePostings'

type PaymentFeePostingsSectionProps = {
  categoryId: number
  emptyMessage: string
  reloadToken?: number
}

export function PaymentFeePostingsSection({
  categoryId,
  emptyMessage,
  reloadToken = 0,
}: PaymentFeePostingsSectionProps) {
  const postings = usePaymentFeePostings(categoryId, reloadToken)

  const campaigns = postings.data?.filter_options.campaigns ?? []
  const institutions = postings.data?.filter_options.institutions ?? []
  const rows = postings.data?.postings ?? []
  const meta = postings.data?.meta
  const filteredTotal = postings.data?.filtered_total ?? 0

  return (
    <section className={styles.postingsSection} aria-labelledby={`payment-fee-postings-${categoryId}`}>
      <h4 id={`payment-fee-postings-${categoryId}`} className={styles.sectionTitle}>
        Lançamentos por venda
      </h4>

      <form
        className={styles.filterBar}
        onSubmit={(event) => {
          event.preventDefault()
          const { from, to } = postings.draftFilters
          if (from !== '' && to !== '' && from > to) {
            return
          }
          postings.applyFilters()
        }}
      >
        <label className={styles.field} htmlFor={`fee-filter-month-${categoryId}`}>
          Mês
          <input
            id={`fee-filter-month-${categoryId}`}
            className={styles.input}
            type="month"
            value={postings.draftFilters.month}
            onChange={(event) =>
              postings.setDraftFilters({
                ...postings.draftFilters,
                month: event.target.value,
                from: '',
                to: '',
              })
            }
          />
        </label>
        <label className={styles.field} htmlFor={`fee-filter-from-${categoryId}`}>
          Data inicial
          <input
            id={`fee-filter-from-${categoryId}`}
            className={styles.input}
            type="date"
            value={postings.draftFilters.from}
            onChange={(event) =>
              postings.setDraftFilters({
                ...postings.draftFilters,
                from: event.target.value,
                month: '',
              })
            }
          />
        </label>
        <label className={styles.field} htmlFor={`fee-filter-to-${categoryId}`}>
          Data final
          <input
            id={`fee-filter-to-${categoryId}`}
            className={styles.input}
            type="date"
            value={postings.draftFilters.to}
            onChange={(event) =>
              postings.setDraftFilters({
                ...postings.draftFilters,
                to: event.target.value,
                month: '',
              })
            }
          />
        </label>
        <label className={styles.field} htmlFor={`fee-filter-institution-${categoryId}`}>
          Entidade
          <select
            id={`fee-filter-institution-${categoryId}`}
            className={styles.input}
            value={postings.draftFilters.institutionId}
            onChange={(event) =>
              postings.setDraftFilters({
                ...postings.draftFilters,
                institutionId: event.target.value,
              })
            }
          >
            <option value="">Todas as entidades</option>
            {institutions.map((institution) => (
              <option key={institution.id} value={String(institution.id)}>
                {institution.name}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field} htmlFor={`fee-filter-campaign-${categoryId}`}>
          Campanha
          <select
            id={`fee-filter-campaign-${categoryId}`}
            className={styles.input}
            value={postings.draftFilters.campaignId}
            onChange={(event) =>
              postings.setDraftFilters({
                ...postings.draftFilters,
                campaignId: event.target.value,
              })
            }
          >
            <option value="">Todas as campanhas</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={String(campaign.id)}>
                {campaign.name}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.filterActions}>
          <button type="submit" className={styles.primaryButton} disabled={postings.isLoading}>
            Filtrar
          </button>
          <button
            type="button"
            className={styles.ghostButton}
            disabled={postings.isLoading}
            onClick={postings.clearFilters}
          >
            Limpar
          </button>
        </div>
      </form>

      {postings.error ? (
        <p className={styles.inlineError} role="alert">
          {postings.error}
        </p>
      ) : null}

      {postings.draftFilters.from !== '' &&
      postings.draftFilters.to !== '' &&
      postings.draftFilters.from > postings.draftFilters.to ? (
        <p className={styles.inlineError} role="alert">
          A data final precisa ser igual ou posterior à data inicial.
        </p>
      ) : null}

      {postings.data !== null && !postings.error ? (
        <p className={styles.filteredTotal}>Total filtrado: {formatOperatingCostAmount(filteredTotal)}</p>
      ) : null}

      {postings.isLoading && postings.data === null ? (
        <p className={styles.empty}>Carregando lançamentos...</p>
      ) : postings.error ? null : rows.length === 0 ? (
        <p className={styles.empty}>{emptyMessage}</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Entidade</th>
                <th scope="col">Campanha</th>
                <th scope="col">Cliente</th>
                <th scope="col">Pagamento</th>
                <th scope="col">Tipo de taxa</th>
                <th scope="col" className={styles.numeric}>
                  Percentual
                </th>
                <th scope="col" className={styles.numeric}>
                  Valor da venda
                </th>
                <th scope="col" className={styles.numeric}>
                  Taxa
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((posting) => (
                <tr key={posting.id}>
                  <td>{formatOperatingCostDateTime(posting.posted_at)}</td>
                  <td>{posting.institution_name}</td>
                  <td>{posting.campaign_name}</td>
                  <td>{posting.donor_name}</td>
                  <td>{formatOperatingCostPaymentFeeOrigin(posting.origin)}</td>
                  <td>{formatOperatingCostPaymentFeeScope(posting.fee_scope)}</td>
                  <td className={styles.numeric}>{formatOperatingCostRate(posting.rate)}</td>
                  <td className={styles.numeric}>{formatOperatingCostAmount(posting.sale_amount)}</td>
                  <td className={styles.numeric}>{formatOperatingCostAmount(posting.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta && meta.last_page > 1 && !postings.error ? (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={postings.isLoading || meta.current_page <= 1}
            onClick={() => postings.goToPage(meta.current_page - 1)}
          >
            Anterior
          </button>
          <p className={styles.paginationStatus}>
            Página {meta.current_page} de {meta.last_page}
          </p>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={postings.isLoading || meta.current_page >= meta.last_page}
            onClick={() => postings.goToPage(meta.current_page + 1)}
          >
            Próxima
          </button>
        </div>
      ) : null}
    </section>
  )
}
